import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { MdOutlineCancel } from 'react-icons/md';
import { useStateContext } from '../../../contexts/ContextProvider';
import { Button } from '../../../components';
import FormInput from '../../../components/FormField';
import { updateDocument, config, uploadFile } from '../../../lib/appwrite';

const EditPhoto = ({ view, setView, selectedRowData, setSelectedRowData, mode, onSuccess }) => {
  const { currentColor } = useStateContext();
  const isMountedRef = useRef(true);
  const fileInputRef = useRef(null);

  const [name, setName] = useState('');
  const [nameError, setNameError] = useState(false);
  const [file, setFile] = useState(null);
  const [previewSrc, setPreviewSrc] = useState('');
  const [fileError, setFileError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    isMountedRef.current = true;

    if (view && selectedRowData && mode === 'edit') {
      setName(selectedRowData.name || '');
      setPreviewSrc(selectedRowData.photoUrl || '');
      setFile(null);
    } else if (view && mode === 'create') {
      // Reset form for create mode
      setName('');
      setFile(null);
      setPreviewSrc('');
    }

    return () => {
      isMountedRef.current = false;
    };
  }, [view, selectedRowData, mode]);

  const close_menu = () => {
    setName('');
    setFile(null);
    setPreviewSrc('');
    setNameError(false);
    setFileError(false);
    setSelectedRowData(null);
    setView(false);
    setIsLoading(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(selectedFile.type)) {
        setFileError(true);
        alert('Please select a valid image file (JPEG, PNG, GIF, or WebP)');
        return;
      }

      // Validate file size (10MB)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setFileError(true);
        alert('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
      setFileError(false);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewSrc(reader.result);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const submit = async () => {
    // Reset errors
    setNameError(false);
    setFileError(false);

    // Validate
    let hasError = false;
    if (name === '') {
      setNameError(true);
      hasError = true;
    }

    // File is required for create mode
    if (mode === 'create' && !file) {
      setFileError(true);
      hasError = true;
    }

    // For edit mode, either file or existing photo is required
    if (mode === 'edit' && !file && !selectedRowData?.photoUrl) {
      setFileError(true);
      hasError = true;
    }

    if (hasError) {
      return;
    }

    try {
      if (!isMountedRef.current) return;
      setIsLoading(true);

      if (mode === 'edit') {
        // For edit mode: if file is uploaded, update the file (which also updates name)
        // Otherwise, just update the name
        if (file) {
          // Upload file to update existing image (this also updates the name)
          await uploadFile(file, config.photosBucketId, selectedRowData.id || selectedRowData.$id, name);
        } else {
          // Update name only (no file upload)
          const data = {
            name: name,
          };
          await updateDocument(selectedRowData.$id || selectedRowData.id, data, config.photosCollectionId);
        }
      } else {
        // For create mode: upload file with name
        if (file) {
          await uploadFile(file, config.photosBucketId, null, name);
        }
      }

      if (!isMountedRef.current) return;

      if (onSuccess) {
        onSuccess();
      }
      close_menu();
    } catch (error) {
      if (!isMountedRef.current) return;
      console.error('Error saving photo:', error);
      alert('Failed to save photo. Please try again.');
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  if (!view) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={close_menu}
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
              {mode === 'create' ? 'Upload New Photo' : 'Edit Photo'}
            </h2>
            <button
              onClick={close_menu}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <MdOutlineCancel className="text-2xl" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            <FormInput
              title="Photo Name"
              placeholder="Enter photo name"
              value={name}
              handleChangeText={setName}
              error={nameError}
              required
            />

            {/* File Upload */}
            <div className="space-y-2">
              <div className="flex">
                <p className="text-base dark:text-gray-100 font-medium">Photo</p>
                <p className="text-red-500 font-medium pl-2">*</p>
              </div>

              <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
                {previewSrc ? (
                  <div className="space-y-4">
                    <div className="relative">
                      <img
                        src={previewSrc}
                        alt="Preview"
                        className="w-full h-64 object-contain rounded-lg"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        setPreviewSrc('');
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      className="text-sm text-red-600 hover:text-red-700 dark:text-red-400"
                    >
                      Remove image
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      {mode === 'create' ? 'Select an image to upload' : 'No new image selected'}
                    </p>
                    {mode === 'edit' && selectedRowData?.photoUrl && (
                      <div className="mb-4">
                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-2">Current photo:</p>
                        <img
                          src={selectedRowData.photoUrl}
                          alt={selectedRowData.name || 'Current photo'}
                          className="w-full h-64 object-contain rounded-lg mx-auto"
                        />
                      </div>
                    )}
                  </div>
                )}

                <div className="mt-4">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                    onChange={handleFileChange}
                    className="hidden"
                    id="file-upload"
                  />
                  <label
                    htmlFor="file-upload"
                    className="cursor-pointer inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
                  >
                    <span>{previewSrc ? 'Change Image' : 'Choose Image'}</span>
                  </label>
                </div>
              </div>

              {fileError && (
                <p className="text-sm text-red-500">Please select an image file</p>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={close_menu}
                disabled={isLoading}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <Button
                bgColor={currentColor || '#1E4DB7'}
                text={mode === 'create' ? 'Upload Photo' : 'Save Changes'}
                borderRadius="8px"
                color="white"
                isLoading={isLoading}
                onClick={submit}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default EditPhoto;
